/**
 * "Can't find a resident?" -- a provider pulls a resident in from CounselEAR (Isaiah Cotto, 2026-10-28;
 * docs/provider/CE_PULL.md).
 *
 * Mirrors backend/src/routes/emergeDaySheetCePull.js:
 *   POST /emerge/day-sheet/:facilityId/ce-pull/lookup   { counselear_id } | { last_name, first_name?, ask_counselear? }
 *   POST /emerge/day-sheet/:facilityId/ce-pull/add      { counselear_id } + Idempotency-Key
 *
 * Both are POSTs so a name never travels in a URL. The server looks in eMerge first; CounselEAR is read only when
 * eMerge holds nobody. The day sheet says whether the control is offered here (provenance.cePull).
 */
import { api } from "../../../services/api";

/** What the day sheet offers (provenance.cePull). Absent before the deploy: nothing is shown. */
export interface CePullOffer {
  allowed: boolean;
  by_name: boolean;
  /** Adding (creating) is open: RESIDENT_NATIVE_CREATION names counselear. Lookups work either way. */
  add_open: boolean;
}

export interface CePullBuilding {
  id: string;
  name: string | null;
  here: boolean;
  covered: boolean;
}

/** A resident eMerge holds. A name only where the provider may see the resident anyway (a building they cover). */
export interface CePullResident {
  patient_id: string | null;
  name: { first_name: string | null; last_name: string | null } | null;
  status: string | null;
  building: CePullBuilding | null;
  /** Where the resident is on this building's worklists (only when here). */
  show: { lens: string; offset: number; position: number } | null;
  lenses?: string[];
  ask: { words: string } | null;
}

/** The minimum to confirm the person: name, date of birth, CounselEAR id, Preferred Clinic. */
export interface CePullPreview {
  counselear_id: string;
  first_name: string | null;
  last_name: string | null;
  dob: string | null;
  clinic: { id: string; name: string | null } | null;
  /** A name lookup's candidate: eMerge already holds this CounselEAR id. */
  in_emerge?: boolean;
}

export type CePullWould = "CREATE" | "LINK" | "REVIEW" | "REVIEW_PENDING" | "REVIEW_RULED" | "NO_NAME" | "REFUSED";
export type CePullPlacement = "CONFIRMED_HERE" | "CONFIRMED_ELSEWHERE" | "NO_BUILDING";

export interface CePullTwlQ {
  routing: string;
  pa: { name: string } | null;
  words: string;
}

export interface CePullLookup {
  outcome:
    | "IN_EMERGE" | "DUPLICATE_IN_EMERGE" | "FOUND_IN_COUNSELEAR" | "NOT_IN_COUNSELEAR" | "NO_MATCH" | "TOO_MANY"
    | "COUNSELEAR_UNAVAILABLE" | "COUNSELEAR_BUSY" | "RATE_LIMITED" | "CLINIC_ELSEWHERE";
  words: string;
  counselear_id?: string;
  /** A name lookup: someone with this last name is not shown (a building the provider does not cover). Never a count. */
  hidden?: boolean;
  merged?: boolean;
  residents?: CePullResident[];
  can_ask_counselear?: boolean;
  preview?: CePullPreview;
  candidates?: CePullPreview[];
  add?: {
    would: CePullWould;
    placement: CePullPlacement | null;
    building: { id: string; name: string | null } | null;
    words: string;
    open: boolean;
    twl_q: CePullTwlQ | null;
  };
}

export interface CePullAdd {
  outcome:
    | "CREATED" | "LINKED_EXISTING" | "DUPLICATE_IN_EMERGE" | "REVIEW_PENDING" | "REVIEW_OPENED" | "REVIEW_RULED"
    | "NOT_IN_COUNSELEAR" | "NO_NAME" | "REFUSED" | "COUNSELEAR_UNAVAILABLE" | "COUNSELEAR_BUSY" | "RATE_LIMITED"
    | "CREATION_CLOSED" | "CLINIC_ELSEWHERE";
  words: string;
  replayed?: boolean;
  placement?: CePullPlacement | null;
  resident?: CePullResident | null;
  twl_q?: CePullTwlQ | null;
}

export interface CePullResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  code?: string;
  status?: number;
}

export type LookupInput =
  | { counselear_id: string }
  | { last_name: string; first_name?: string; ask_counselear?: boolean };

/** One key per add attempt: a retry after a lost answer is the same add, never a second one. */
export function newPullKey(): string {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `cep-${c.randomUUID()}`;
  return `cep-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const base = (facilityId: string) => `/emerge/day-sheet/${encodeURIComponent(facilityId)}/ce-pull`;

export const cePullApi = {
  lookup: async (facilityId: string, input: LookupInput): Promise<CePullResult<CePullLookup>> =>
    (await api.post(`${base(facilityId)}/lookup`, input)) as CePullResult<CePullLookup>,
  add: async (facilityId: string, counselearId: string, key: string): Promise<CePullResult<CePullAdd>> =>
    (await api.post(`${base(facilityId)}/add`, { counselear_id: counselearId, confirmed_with_resident: true }, { headers: { "Idempotency-Key": key } })) as CePullResult<CePullAdd>,
};

/** A day of birth as the screen shows it ("Feb 3, 1940"); the server sends a bare day. Never shifted by a zone. */
export function dobWords(day: string | null | undefined): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return "not readable in CounselEAR";
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}
